import { useState, useEffect, useCallback } from "react";
import { api } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  Wallet, Bot, ShoppingCart, TrendingUp, RefreshCw, Activity, CircleDot,
  BarChart3, ArrowLeftRight, Trash2
} from "lucide-react";

function StatCard({ title, value, subtitle, icon: Icon, variant = "default" }) {
  const colors = {
    default: "text-foreground",
    profit: "text-emerald-500",
    loss: "text-red-500",
    primary: "text-primary",
    amber: "text-amber-400",
    cyan: "text-cyan-400",
  };
  return (
    <div className="stat-card animate-fade-in" data-testid={`stat-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{title}</span>
        <Icon className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
      </div>
      <p className={`text-2xl font-bold font-mono tabular-nums ${colors[variant]}`}>{value}</p>
      {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
    </div>
  );
}

function formatVolume(v) {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [volumePeriod, setVolumePeriod] = useState("today");
  const [clearingLogs, setClearingLogs] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const r = await api.get("/dashboard");
      setData(r.data);
    } catch (err) {
      console.error("Dashboard fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const clearAllLogs = async () => {
    if (!window.confirm("Are you sure you want to delete ALL activity logs? This action cannot be undone.")) {
      return;
    }

    setClearingLogs(true);
    try {
      const response = await api.delete("/logs");
      toast.success(response.data.message || `Deleted ${response.data.deleted_count} logs successfully`);
      // Refresh dashboard to update activity log section
      await fetchDashboard();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to clear logs");
      console.error("Clear logs error:", err);
    } finally {
      setClearingLogs(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    const iv = setInterval(fetchDashboard, 15000);
    return () => clearInterval(iv);
  }, [fetchDashboard]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-lg" />)}
        </div>
        <Skeleton className="h-64 rounded-lg" />
      </div>
    );
  }

  const d = data || {};
  const volumes = d.volumes || {};
  const vp = volumes[volumePeriod === "today" ? "today" : "all_time"] || {};

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-0.5">NOMOX/USDT Market Making Overview</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchDashboard} 
            data-testid="refresh-dashboard-btn" 
            className="w-full sm:w-auto"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-2" />
            Refresh
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={clearAllLogs}
            disabled={clearingLogs}
            data-testid="clear-logs-btn" 
            className="w-full sm:w-auto text-red-500 hover:text-red-600 hover:bg-red-500/10"
          >
            <Trash2 className="w-3.5 h-3.5 mr-2" />
            {clearingLogs ? "Clearing..." : "Clear Logs"}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="USDT Balance" value={`$${d.total_balance_usdt?.toFixed(2) || "0.00"}`} subtitle="Total available" icon={Wallet} variant="primary" />
        <StatCard title="NOMOX Balance" value={Number(d.base_balance || 0).toLocaleString()} subtitle="Base asset" icon={TrendingUp} />
        <StatCard title="Active Bots" value={`${d.active_bots || 0} / ${d.total_bots || 0}`} subtitle="Running / Total" icon={Bot} variant={d.active_bots > 0 ? "profit" : "default"} />
        <StatCard title="Open Orders" value={d.open_orders || 0} subtitle="On MEXC" icon={ShoppingCart} />
      </div>

      {/* Volume Section */}
      <Card className="bg-card border-border/50" data-testid="volume-section">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Trading Volume
            </CardTitle>
            <div className="flex items-center rounded-md border border-border/50 overflow-hidden">
              <button
                className={`px-3 py-1 text-[10px] uppercase tracking-wider font-semibold transition-colors ${
                  volumePeriod === "today"
                    ? "bg-primary/15 text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => setVolumePeriod("today")}
                data-testid="volume-toggle-today"
              >
                Today
              </button>
              <button
                className={`px-3 py-1 text-[10px] uppercase tracking-wider font-semibold transition-colors ${
                  volumePeriod === "all_time"
                    ? "bg-primary/15 text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => setVolumePeriod("all_time")}
                data-testid="volume-toggle-alltime"
              >
                All Time
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Total Volume */}
            <div className="p-4 rounded-lg bg-secondary/30 border border-border/30">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="w-4 h-4 text-amber-400" />
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Total Volume</span>
              </div>
              <p className="text-3xl font-bold font-mono tabular-nums text-amber-400" data-testid="total-volume-value">
                {formatVolume(vp.total || 0)}
              </p>
              <p className="text-[10px] text-muted-foreground mt-1">
                All bot orders ({volumePeriod === "today" ? "24h" : "lifetime"})
              </p>
            </div>

            {/* Non-Self-Trade Volume */}
            <div className="p-4 rounded-lg bg-secondary/30 border border-cyan-500/20">
              <div className="flex items-center gap-2 mb-2">
                <ArrowLeftRight className="w-4 h-4 text-cyan-400" />
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Non-Self-Trade Volume</span>
              </div>
              <p className="text-3xl font-bold font-mono tabular-nums text-cyan-400" data-testid="nst-volume-value">
                {formatVolume(vp.non_self_trade || 0)}
              </p>
              <p className="text-[10px] text-muted-foreground mt-1">
                Cross-account matched trades ({volumePeriod === "today" ? "24h" : "lifetime"})
              </p>
            </div>
          </div>

          {/* Volume Ratio Bar */}
          {(vp.total || 0) > 0 && (
            <div className="mt-4" data-testid="volume-ratio-bar">
              <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1.5">
                <span>Non-Self-Trade Ratio</span>
                <span className="font-mono text-cyan-400">
                  {((vp.non_self_trade || 0) / (vp.total || 1) * 100).toFixed(1)}%
                </span>
              </div>
              <div className="h-2 bg-secondary/50 rounded-full overflow-hidden">
                <div
                  className="h-full bg-cyan-500 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(((vp.non_self_trade || 0) / (vp.total || 1)) * 100, 100)}%` }}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bot Summary + Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Bot Summary */}
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Bot className="w-4 h-4" />
              Bot Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            {d.bot_summary?.length > 0 ? (
              <div className="space-y-2">
                {d.bot_summary.map((bot) => (
                  <div key={bot.id} className="flex items-center justify-between py-2 px-3 rounded-md bg-secondary/30">
                    <div className="flex items-center gap-2">
                      <CircleDot className={`w-3 h-3 ${bot.status === "running" ? "text-emerald-500" : "text-muted-foreground"}`} />
                      <span className="text-sm font-medium">{bot.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{bot.type.replace(/_/g, " ")}</span>
                      <Badge variant={bot.status === "running" ? "default" : "secondary"} className={`text-[10px] ${bot.status === "running" ? "bg-emerald-500/15 text-emerald-500 border-transparent" : ""}`}>
                        {bot.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Bot className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No bots configured yet</p>
                <p className="text-xs mt-1">Go to Bot Management to create one</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {d.recent_logs?.length > 0 ? (
              <div className="space-y-1.5 max-h-[320px] overflow-y-auto">
                {d.recent_logs.map((log) => (
                  <div key={log.id} className="flex items-start gap-2 py-1.5 px-2 rounded text-xs">
                    <span className={`font-mono text-[10px] mt-0.5 px-1.5 py-0.5 rounded ${
                      log.action === "error" ? "bg-red-500/15 text-red-400" :
                      log.action === "started" ? "bg-emerald-500/15 text-emerald-400" :
                      log.action === "stopped" ? "bg-amber-500/15 text-amber-400" :
                      log.action === "cross_order" ? "bg-cyan-500/15 text-cyan-400" :
                      "bg-secondary text-muted-foreground"
                    }`}>
                      {log.action}
                    </span>
                    <span className="text-muted-foreground flex-1 truncate">{log.message}</span>
                    <span className="text-[10px] text-muted-foreground/60 font-mono whitespace-nowrap">
                      {new Date(log.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Activity className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No activity yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
